import type { PageRef, Rect } from '@/types'

/** Latest rendered canvas for each page id — used to sample colors for text editing. */
const canvases = new Map<string, HTMLCanvasElement>()

export const registerCanvas = (pageId: string, c: HTMLCanvasElement) => canvases.set(pageId, c)
export const unregisterCanvas = (pageId: string, c?: HTMLCanvasElement) => {
  if (!c || canvases.get(pageId) === c) canvases.delete(pageId)
}
export const getCanvas = (pageId: string) => canvases.get(pageId)

const hex = (r: number, g: number, b: number) =>
  `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`

/**
 * Sample the background (most common edge color) and foreground (most contrasting color)
 * inside a box of the rendered page.
 */
export function sampleColors(page: PageRef, box: Rect): { bg: string; fg: string } {
  const fallback = { bg: '#ffffff', fg: '#000000' }
  const canvas = canvases.get(page.id)
  if (!canvas || !canvas.width) return fallback
  const k = canvas.width / page.width
  const x = Math.max(0, Math.floor((box.x - 1) * k))
  const y = Math.max(0, Math.floor((box.y - 1) * k))
  const w = Math.min(canvas.width - x, Math.ceil((box.w + 2) * k))
  const h = Math.min(canvas.height - y, Math.ceil((box.h + 2) * k))
  if (w <= 2 || h <= 2) return fallback
  let data: Uint8ClampedArray
  try {
    data = canvas.getContext('2d')!.getImageData(x, y, w, h).data
  } catch {
    return fallback
  }
  const counts = new Map<number, { n: number; r: number; g: number; b: number }>()
  const add = (i: number) => {
    const r = data[i]
    const g = data[i + 1]
    const b = data[i + 2]
    const key = ((r >> 4) << 8) | ((g >> 4) << 4) | (b >> 4)
    const c = counts.get(key)
    if (c) {
      c.n++
      c.r += r
      c.g += g
      c.b += b
    } else counts.set(key, { n: 1, r, g, b })
  }
  for (let i = 0; i < w; i++) {
    add(i * 4)
    add(((h - 1) * w + i) * 4)
  }
  for (let j = 0; j < h; j++) {
    add(j * w * 4)
    add((j * w + w - 1) * 4)
  }
  let best = { n: 0, r: 255, g: 255, b: 255 }
  for (const c of counts.values()) if (c.n > best.n) best = c
  const bg = [best.r / best.n, best.g / best.n, best.b / best.n].map(Math.round) as [number, number, number]

  let maxD = 0
  for (let i = 0; i < data.length; i += 4) {
    const d = Math.abs(data[i] - bg[0]) + Math.abs(data[i + 1] - bg[1]) + Math.abs(data[i + 2] - bg[2])
    if (d > maxD) maxD = d
  }
  if (maxD < 40) return { bg: hex(...bg), fg: bg[0] + bg[1] + bg[2] > 382 ? '#000000' : '#ffffff' }
  let r = 0
  let g = 0
  let b = 0
  let n = 0
  for (let i = 0; i < data.length; i += 4) {
    const d = Math.abs(data[i] - bg[0]) + Math.abs(data[i + 1] - bg[1]) + Math.abs(data[i + 2] - bg[2])
    if (d >= maxD * 0.8) {
      r += data[i]
      g += data[i + 1]
      b += data[i + 2]
      n++
    }
  }
  return { bg: hex(...bg), fg: hex(Math.round(r / n), Math.round(g / n), Math.round(b / n)) }
}
